import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pulseRoute} from './pulse-route'
import {PulseHeader} from '../components/PulseHeader'
import {fetchPoll} from '@github-ui/fetch-utils'
import {useEffect, useState, useCallback} from 'react'
import {PulseOverview} from '../components/PulseOverview'
import {PulseSummary} from '../components/PulseSummary'
import {PullRequestSection} from '../components/PullRequestSection'
import {IssueSection} from '../components/IssueSection'
import {UnresolvedConversations} from '../components/UnresolvedConversations'
import {Releases} from '../components/Releases'
import type {PulseAuthorCommitsDataPoint, PulseOverviewDataPoint, PulseDiffstatSummary} from '../types'
import {defaultOverviewData, defaultDiffstatSummary} from '../types'
import {Stack} from '@primer/react'
import {SkeletonLoaderOverview} from '../components/loaders/OverviewLoaders'
import {PulsePeriodProvider, usePulsePeriod} from '../contexts/PulsePeriodContext'
import {ErrorMessage} from '../components/ErrorMessage'

function PulseContent() {
  const payload = useRouteQuery(pulseRoute, 'mainQuery')

  // State to track loading status for different sections
  const [isOverviewLoaded, setIsOverviewLoaded] = useState(false)
  const [isSummaryLoaded, setIsSummaryLoaded] = useState(false)
  const [isChartLoaded, setIsChartLoaded] = useState(false)

  // State to track individual errors for each section
  const [summaryError, setSummaryError] = useState<string | undefined>()
  const [chartError, setChartError] = useState<string | undefined>()
  const [overviewError, setOverviewError] = useState<string | undefined>()

  // State to hold data for different sections
  const [overviewData, setOverviewData] = useState<PulseOverviewDataPoint>(defaultOverviewData)
  const [authorCommits, setAuthorCommits] = useState<PulseAuthorCommitsDataPoint[]>([])
  const [diffstatSummary, setDiffstatSummary] = useState<PulseDiffstatSummary>(defaultDiffstatSummary)

  // Paths for fetching data
  const committerDataPath = payload.data?.committerDataPath
  const diffstatDataPath = payload.data?.diffstatSummaryPath
  const overviewDataPath = payload.data?.overviewDataPath

  const period_conversions: Record<string, string> = {
    '24 hours': 'daily',
    '3 days': 'halfweekly',
    '1 week': 'weekly',
    '1 month': 'monthly',
  }
  const {selectedPeriod} = usePulsePeriod()
  const currentPeriod = period_conversions[selectedPeriod] || 'weekly'

  // Generic fetch function
  const fetchData = useCallback(
    async <T,>(
      dataPath: string | undefined,
      dataType: string,
      setData: (data: T) => void,
      setLoaded: (loaded: boolean) => void,
      setError?: (error: string | undefined) => void,
    ): Promise<void> => {
      if (!dataPath) {
        if (setError) {
          setError(`${dataType} data path is not available`)
        }
        setLoaded(true)
        return
      }

      try {
        const url = `${dataPath}/${currentPeriod}`
        const response = await fetchPoll(url)

        if (response.ok) {
          const data: T = await response.json()
          setData(data)
          if (setError) {
            setError(undefined) // Clear error on successful fetch
          }
        } else {
          let message = 'There was an error fetching the data'
          if (response.status >= 500) {
            message = 'Server error occurred. Please try again later.'
          } else if (response.status === 404) {
            message = 'Data not found.'
          } else if (response.status >= 400) {
            message = 'Bad request. Please refresh the page and try again.'
          }

          try {
            const {unusable} = await response.json()
            if (unusable) {
              message = 'Data is unusable'
            }
          } catch {
            // Keep the default error message based on status code
          }

          if (setError) {
            setError(message)
          }
        }
      } catch (error) {
        const message =
          error instanceof Error && error.name === 'AbortError'
            ? 'Request was cancelled. Please try again.'
            : 'An unexpected error occurred while fetching the data'

        if (setError) {
          setError(message)
        }
      } finally {
        setLoaded(true)
      }
    },
    [currentPeriod],
  )

  // Reset state when period changes, shows loading state for cleaner transition
  useEffect(() => {
    setIsOverviewLoaded(false)
    setIsSummaryLoaded(false)
    setIsChartLoaded(false)
    setOverviewData(defaultOverviewData)
    setAuthorCommits([])
    setDiffstatSummary(defaultDiffstatSummary)
    setSummaryError(undefined)
    setChartError(undefined)
    setOverviewError(undefined)
  }, [currentPeriod])

  // Fetch author committer data
  useEffect(() => {
    fetchData<PulseAuthorCommitsDataPoint[]>(
      committerDataPath,
      'Committer',
      setAuthorCommits,
      setIsChartLoaded,
      setChartError,
    )
  }, [committerDataPath, currentPeriod, fetchData])

  // Fetch diffstat summary data - always fetch to get forkPathUrl even if no commits
  useEffect(() => {
    fetchData<PulseDiffstatSummary>(
      diffstatDataPath,
      'Diffstat',
      setDiffstatSummary,
      setIsSummaryLoaded,
      setSummaryError,
    )
  }, [diffstatDataPath, currentPeriod, fetchData])

  // Fetch overview data
  useEffect(() => {
    fetchData<PulseOverviewDataPoint>(
      overviewDataPath,
      'Overview',
      setOverviewData,
      setIsOverviewLoaded,
      setOverviewError,
    )
  }, [overviewDataPath, currentPeriod, fetchData])

  // Only show global error for errors that prevent the whole page from working
  if (overviewError && summaryError && chartError) {
    return <ErrorMessage errorMessage={overviewError} />
  }

  return (
    <Stack gap={'condensed'}>
      <PulseHeader rangeLabel={overviewData.rangeLabel} loaded={isOverviewLoaded} data-hpc />
      {isOverviewLoaded ? <PulseOverview overviewData={overviewData} /> : <SkeletonLoaderOverview />}

      <PulseSummary
        committerData={authorCommits}
        diffstatSummary={diffstatSummary}
        summaryLoaded={isSummaryLoaded}
        chartLoaded={isChartLoaded}
        summaryError={summaryError}
        chartError={chartError}
      />

      {isOverviewLoaded ? (
        <>
          <Releases
            count={overviewData.releases.length}
            peopleCount={overviewData.numAuthorsReleases}
            releases={overviewData.releases}
            data-hpc
          />
          <PullRequestSection
            type="merged"
            count={overviewData.mergedPulls.length}
            peopleCount={overviewData.numAuthorsMergedPulls}
            pullRequests={overviewData.mergedPulls}
          />
          <PullRequestSection
            type="opened"
            count={overviewData.newPulls.length}
            peopleCount={overviewData.numAuthorsNewPulls}
            pullRequests={overviewData.newPulls}
          />
          <IssueSection
            type="closed"
            count={overviewData.closedIssues.length}
            peopleCount={overviewData.numAuthorsClosedIssues}
            issues={overviewData.closedIssues}
          />
          <IssueSection
            type="opened"
            count={overviewData.newIssues.length}
            peopleCount={overviewData.numAuthorsNewIssues}
            issues={overviewData.newIssues}
          />
          <UnresolvedConversations count={overviewData.discussions.length} conversations={overviewData.discussions} />
        </>
      ) : null}
    </Stack>
  )
}

export function Pulse() {
  return (
    <PulsePeriodProvider>
      <PulseContent />
    </PulsePeriodProvider>
  )
}

try{ PulseContent.displayName ||= 'PulseContent' } catch {}
try{ Pulse.displayName ||= 'Pulse' } catch {}